import { Box, Text } from '@chakra-ui/react';
import cn from 'classnames';
import { produce } from 'immer';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { createParser, Options, useQueryState } from 'nuqs';
import React, { useTransition } from 'react';

import {
  BodyTypeList,
  ColourTypeList,
  DoorTypeList,
} from '@/services/filters/new-filters.service';
import { getImageLink } from '@/services/link.service';

import { NewFilter } from '../new-filter/new-filter';
import { isFilterEqual, useClearSearch } from '../utils';

import styles from './new-body.module.scss';

export const NewBody: React.FC<{
  bodyTypes: BodyTypeList;
  colourTypes: ColourTypeList;
  doorTypes: DoorTypeList;
  count: number;
}> = ({ bodyTypes = [], colourTypes, doorTypes, count }) => {
  const { bodyType: bodyParam } = useParams();

  const bodyType = (() => {
    if (!bodyParam) return undefined;
    if (typeof bodyParam === 'string') return bodyParam.toLowerCase();
    return bodyParam.at(0)?.toLowerCase();
  })();

  const mappingFunction = <T extends { name: string }>(
    item: T,
  ): T & { isChecked: boolean } => {
    return {
      isChecked: item.name.toLowerCase() === bodyType,
      ...item,
    };
  };

  const createMapParser = <T extends { name: string; isChecked: boolean }>(
    items: T[],
  ) => {
    return createParser({
      parse(queryValue) {
        return items.map((item) => {
          item.isChecked = queryValue
            .toLowerCase()
            .includes(item.name.toLowerCase());
          return item;
        });
      },
      serialize(value) {
        return value
          .filter((item) => item.isChecked)
          .map((item) => item.name)
          .join(',');
      },
      eq(a, b) {
        return isFilterEqual(a, b);
      },
    });
  };

  const [_, startTransition] = useTransition();

  const mappedBodyTypes = bodyTypes.map(mappingFunction);
  const parseBodyTypes = createMapParser(mappedBodyTypes);
  const [queryBodyTypes, setQueryBodyTypes] = useQueryState(
    'bodytype',
    parseBodyTypes
      .withDefault(mappedBodyTypes)
      .withOptions({ startTransition, shallow: false }),
  );

  const mappedColourTypes = colourTypes.map(mappingFunction);
  const parseColourTypes = createMapParser(mappedColourTypes);
  const [queryColourTypes, setQueryColourTypes] = useQueryState(
    'colours',
    parseColourTypes
      .withDefault(mappedColourTypes)
      .withOptions({ startTransition, shallow: false }),
  );

  const mappedDoorTypes = doorTypes.map(mappingFunction);
  const parseDoorTypes = createMapParser(mappedDoorTypes);
  const [queryDoorTypes, setQueryDoorTypes] = useQueryState(
    'doors',
    parseDoorTypes
      .withDefault(mappedDoorTypes)
      .withOptions({ startTransition, shallow: false }),
  );

  const clearSearch = useClearSearch();

  const toggleFilter = async (
    item: { name: string; isDisabled?: boolean },
    setter: (
      value: any[] | ((old: any[]) => any[] | null) | null,
      options?: Options,
    ) => Promise<URLSearchParams>,
  ) => {
    if (item.isDisabled) return;
    clearSearch();
    await setter((options) => {
      return produce(options, (draftOptions) => {
        draftOptions?.forEach((option) => {
          if (option.name === item.name) {
            option.isChecked = !option.isChecked;
          }
        });
      });
    });
  };

  return (
    <NewFilter
      label="Body"
      placement="bottom-start"
      count={count}
      onClear={() => {
        setQueryBodyTypes(null);
        setQueryDoorTypes(null);
        setQueryColourTypes(null);
      }}
    >
      <Box className={styles.bodyTypeWrapper}>
        <Text className={styles.heading}>Body Types</Text>
        <Box className={styles.bodyList}>
          {queryBodyTypes.map((body) => {
            return (
              <Box
                key={body.id}
                onClick={() => toggleFilter(body, setQueryBodyTypes)}
                className={cn(
                  styles.bodyTypeItem,
                  body.isChecked && styles.bodyTypeItemSelected,
                  body.isDisabled && styles.bodyTypeDisabled,
                )}
              >
                <Image
                  src={getImageLink(body.image) || ''}
                  alt={body.name}
                  width={82}
                  height={42}
                  className={styles.bodyImage}
                />
                <Text className={styles.bodyTypeText}>
                  {body.name}
                  <Text as="span" className={styles.filterCount}>
                    {`(${body.count})`}
                  </Text>
                </Text>
              </Box>
            );
          })}
        </Box>
        <Text className={styles.heading}>Colour</Text>
        <Box className={styles.bodyList}>
          {queryColourTypes.map((colour) => {
            return (
              <Box
                key={colour.id}
                onClick={() => toggleFilter(colour, setQueryColourTypes)}
                className={cn(
                  styles.bodyTypeItem,
                  colour.isChecked && styles.bodyTypeItemSelected,
                  colour?.isDisabled && styles.bodyTypeDisabled,
                )}
              >
                <div
                  className={cn(styles.colourCircle, {
                    [styles.withBorder as string]:
                      colour.name?.toLowerCase() === 'white',
                  })}
                  style={{ backgroundColor: colour.colour || 'white' }}
                />
                <Text className={styles.bodyTypeText}>
                  {colour.name}
                  <Text as="span" className={styles.filterCount}>
                    {`(${colour.vehicles.length})`}
                  </Text>
                </Text>
              </Box>
            );
          })}
        </Box>
        <Text className={styles.heading}>Doors</Text>
        <Box className={styles.bodyList}>
          {queryDoorTypes.map((door) => {
            return (
              <Box
                key={door.id}
                onClick={() => toggleFilter(door, setQueryDoorTypes)}
                className={cn(
                  styles.bodyTypeItem,
                  styles.doorItem,
                  door.isChecked && styles.doorSelected,
                  door.isDisabled && styles.bodyTypeDisabled,
                )}
              >
                <Text className={styles.bodyTypeText}>
                  {door.name}
                  <Text as="span" className={styles.filterCountDoor}>
                    {`(${door.count})`}
                  </Text>
                </Text>
              </Box>
            );
          })}
        </Box>
      </Box>
    </NewFilter>
  );
};
